import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Briefcase,
  Calendar,
  DollarSign,
  Users,
  FileText,
  Edit,
  Plus,
  FolderOpen,
  Clock,
  MessageSquare,
  Bot,
  StickyNote,
  Receipt,
  Landmark,
  X,
  Search,
  Shield,
  Lock,
  MapPin,
  Maximize2
} from 'lucide-react';
import { caseService, projectService, documentService, casePartyService, userService, clientService } from '../../services/api';
import DocumentUpload from '../../components/documents/DocumentUpload';
import DocumentList from '../../components/documents/DocumentList';
import DocumentPreview from '../../components/documents/DocumentPreview';
import FolderTree from '../../components/documents/FolderTree';
import NewFolderModal from '../../components/documents/NewFolderModal';
import { folderService } from '../../services/api';
import AIChatInterface from '../../components/ai-chat/AIChatInterface';
import { CaseNotesSection } from '../../components/case-notes';
import { DeadlineManager, CaseTimeline, SchedulingRiskDashboard } from '../../components/scheduling';
import CaseTimeTracking from '../../components/time-tracking/CaseTimeTracking';
import CaseExpenses from '../../components/expenses/CaseExpenses';
import useNotification from '../../hooks/useNotification';
import Notification from '../../components/common/Notification';
import Breadcrumb from '../../components/common/Breadcrumb';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import CasePartiesSection from '../../components/case-parties/CasePartiesSection';
import CaseTrustSection from '../../components/trust/CaseTrustSection';
import CaseCalendarEvents from '../../components/calendar/CaseCalendarEvents';
import CaseCommunicationsSection from '../../components/communications/CaseCommunicationsSection';
import SendDocumentModal from '../../components/signing/SendDocumentModal';
import CaseVisibilityModal from '../../components/CaseVisibilityModal';

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { notification, showSuccess, showError, showWarning, hideNotification } = useNotification();
  const [autoOpenNoteEditor, setAutoOpenNoteEditor] = useState(false);
  const [caseData, setCaseData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [caseClients, setCaseClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentsKey, setDocumentsKey] = useState(0); // For refreshing document list
  const [foldersKey, setFoldersKey] = useState(0); // For refreshing folder tree

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [showVisibilityModal, setShowVisibilityModal] = useState(false);

  // Folder states
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [parentFolderForNew, setParentFolderForNew] = useState(null);
  const [showDeleteFolderConfirm, setShowDeleteFolderConfirm] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  // Document preview state
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);

  // Client picker state
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const clientPickerRef = useRef(null);
  const clientSearchDebounceRef = useRef(null);

  // Form states
  const [editForm, setEditForm] = useState({});
  const [attorneys, setAttorneys] = useState([]);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    estimated_value: '',
    estimated_hours: '',
    status: 'ready',
    priority: 'medium',
    start_date: '',
    due_date: '',
    requires_payment_first: true
  });

  const fetchCaseData = useCallback(async () => {
    try {
      const response = await caseService.getCase(id);
      setCaseData(response.data);
      // Store context for Tools page
      localStorage.setItem('lastViewedCase', JSON.stringify({
        caseId: id,
        caseTitle: response.data.title,
        caseNumber: response.data.case_number,
      }));
      localStorage.removeItem('lastViewedProject');
    } catch (error) {
      console.error('Error fetching case:', error);
      setError('Failed to load case details');
    }
  }, [id]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await projectService.getProjectsByCase(id);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Loads the merged set of clients to display in the Client Information section.
  // A case can have clients in two places: (1) the parent client via cases.client_id,
  // and (2) additional clients added through case_parties with role='client'. Both
  // should appear here. The Parties section excludes role='client' separately.
  const fetchCaseClients = useCallback(async () => {
    try {
      const partiesResponse = await casePartyService.getParties(id);
      const partyClients = (partiesResponse.data || []).filter(p => p.role === 'client' && p.client);

      // Always include the parent client (cases.client_id) if present and not already
      // represented by a case_parties row. We synthesize a party-shaped wrapper so the
      // existing render code below works unchanged.
      let combined = partyClients;
      if (caseData?.client_id) {
        const alreadyIncluded = partyClients.some(p => p.client.id === caseData.client_id);
        if (!alreadyIncluded) {
          try {
            const parentResp = await clientService.getClient(caseData.client_id);
            const parentEntry = {
              id: `parent-${caseData.client_id}`,
              role: 'client',
              client: parentResp.data,
            };
            combined = [parentEntry, ...partyClients];
          } catch (err) {
            console.error('Error fetching parent client:', err);
          }
        }
      }
      setCaseClients(combined);
    } catch (error) {
      console.error('Error fetching case clients:', error);
    }
  }, [id, caseData?.client_id]);

  useEffect(() => {
    fetchCaseData();
    fetchProjects();
    fetchCaseClients();
  }, [fetchCaseData, fetchProjects, fetchCaseClients]);

  // Handle quick-action openAction state from navbar
  useEffect(() => {
    const openAction = location.state?.openAction;
    if (!openAction || loading) return;
    if (openAction === 'new-note') {
      setAutoOpenNoteEditor(true);
      const notesSection = document.getElementById('section-notes');
      if (notesSection) notesSection.scrollIntoView({ behavior: 'smooth' });
      navigate(location.pathname, { replace: true, state: {} });
    } else if (openAction === 'new-task' && projects.length > 0) {
      navigate(`/projects/${projects[0].id}`, { state: { openAction: 'new-task' } });
    }
  }, [location.state, loading, projects, navigate, location.pathname]);

  // Close client picker on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientPickerRef.current && !clientPickerRef.current.contains(e.target)) {
        setShowClientPicker(false);
        setClientSearch('');
        setClientResults([]);
      }
    };
    if (showClientPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showClientPicker]);

  // Search clients for the picker
  const handleClientSearch = (query) => {
    setClientSearch(query);
    if (clientSearchDebounceRef.current) clearTimeout(clientSearchDebounceRef.current);
    if (!query.trim()) {
      setClientResults([]);
      return;
    }
    clientSearchDebounceRef.current = setTimeout(async () => {
      setClientSearchLoading(true);
      try {
        const res = await clientService.getClients({ search: query, limit: 20 });
        const clients = res.data?.data || res.data || [];
        // Filter out clients already on this case
        const existingClientIds = caseClients.map(p => p.client?.id).filter(Boolean);
        setClientResults(clients.filter(c => !existingClientIds.includes(c.id)));
      } catch (err) {
        console.error('Error searching clients:', err);
      } finally {
        setClientSearchLoading(false);
      }
    }, 300);
  };

  // Add a client to this case
  const handleAddClient = async (clientId) => {
    try {
      await casePartyService.addParty(id, { client_id: clientId, role: 'client' });
      await fetchCaseClients();
      setShowClientPicker(false);
      setClientSearch('');
      setClientResults([]);
      showSuccess('Client added to case');
    } catch (err) {
      console.error('Error adding client:', err);
      showError(err.response?.data?.detail || 'Failed to add client');
    }
  };

  // Remove a client party from this case
  const handleRemoveClient = async (partyId) => {
    try {
      await casePartyService.removeParty(id, partyId);
      await fetchCaseClients();
      showSuccess('Client removed from case');
    } catch (err) {
      console.error('Error removing client:', err);
      showError(err.response?.data?.detail || 'Failed to remove client');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProjectStatusColor = (status) => {
    switch (status) {
      case 'planned':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'on_hold':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEditCase = async () => {
    if (caseData) {
      setEditForm({
        case_number: caseData.case_number || '',
        title: caseData.title || '',
        description: caseData.description || '',
        case_type: caseData.case_type || '',
        status: caseData.status || 'active',
        total_estimated_value: caseData.total_estimated_value || '',
        billing_type: caseData.billing_type || 'hourly',
        flat_fee_amount: caseData.flat_fee_amount || '',
        contingency_percentage: caseData.contingency_percentage || '',
        requires_trust_funds: caseData.requires_trust_funds !== false,
        assigned_attorney_id: caseData.assigned_attorney_id || ''
      });
      setShowEditModal(true);
      try {
        const res = await userService.getUsers(false, 'Attorney');
        const active = (res.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
        setAttorneys(active);
      } catch (err) {
        console.error('Could not load attorneys:', err);
      }
    }
  };

  const handleSaveCase = async () => {
    try {
      const updateData = {
        ...editForm,
        total_estimated_value: editForm.total_estimated_value ? parseFloat(editForm.total_estimated_value) : null,
        flat_fee_amount: editForm.flat_fee_amount ? parseFloat(editForm.flat_fee_amount) : null,
        contingency_percentage: editForm.contingency_percentage ? parseFloat(editForm.contingency_percentage) : null
      };

      // Remove empty string fields
      Object.keys(updateData).forEach(key => {
        if (updateData[key] === '') {
          updateData[key] = null;
        }
      });

      await caseService.updateCase(id, updateData);
      setShowEditModal(false);
      await fetchCaseData();
      showSuccess('Case updated successfully!');
    } catch (error) {
      console.error('Error updating case:', error);
      showError(`Failed to update case: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleCreateProject = async () => {
    try {
      if (!projectForm.name) {
        showWarning('Project name is required');
        return;
      }

      const projectData = {
        ...projectForm,
        case_id: id,
        estimated_value: projectForm.estimated_value ? parseFloat(projectForm.estimated_value) : 0,
        estimated_hours: projectForm.estimated_hours ? parseFloat(projectForm.estimated_hours) : null,
        start_date: projectForm.start_date || null,
        due_date: projectForm.due_date || null
      };

      // Remove empty string fields
      Object.keys(projectData).forEach(key => {
        if (projectData[key] === '') {
          projectData[key] = null;
        }
      });

      await projectService.createProject(projectData);
      setShowProjectModal(false);
      setProjectForm({
        name: '',
        description: '',
        estimated_value: '',
        estimated_hours: '',
        status: 'ready',
        priority: 'medium',
        start_date: '',
        due_date: '',
        requires_payment_first: true
      });
      await fetchProjects();
      showSuccess('Project created successfully!');
    } catch (error) {
      console.error('Error creating project:', error);
      showError(`Failed to create project: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDocumentUpload = useCallback(() => {
    // Refresh documents list
    setDocumentsKey(prev => prev + 1);
  }, []);

  // Folder handlers
  const handleSelectFolder = (folder) => {
    setSelectedFolder(folder);
  };

  const handleCreateFolder = (parentFolder) => {
    setParentFolderForNew(parentFolder);
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder) => {
    setEditingFolder(folder);
    setParentFolderForNew(null);
    setShowFolderModal(true);
  };

  const handleSaveFolder = async (folderData) => {
    try {
      if (editingFolder) {
        await folderService.updateFolder(editingFolder.id, folderData);
        showSuccess('Folder updated successfully');
      } else {
        await folderService.createFolder(folderData);
        showSuccess('Folder created successfully');
      }
      // Refresh both folder tree and document list
      setFoldersKey(prev => prev + 1);
      setDocumentsKey(prev => prev + 1);
    } catch (err) {
      throw err; // Re-throw to let modal handle error
    }
  };

  const handleDeleteFolder = (folder) => {
    setFolderToDelete(folder);
    setShowDeleteFolderConfirm(true);
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;

    try {
      await folderService.deleteFolder(folderToDelete.id, true);
      showSuccess('Folder deleted successfully');

      // Clear selection if deleted folder was selected
      if (selectedFolder?.id === folderToDelete.id) {
        setSelectedFolder(null);
      }

      // Refresh both folder tree and document list
      setFoldersKey(prev => prev + 1);
      setDocumentsKey(prev => prev + 1);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to delete folder');
    } finally {
      setShowDeleteFolderConfirm(false);
      setFolderToDelete(null);
    }
  };

  const handleDropDocument = useCallback(async (documentId, folderId) => {
    try {
      await documentService.updateDocument(documentId, {
        folder_id: folderId
      });

      const folderName = folderId === null ? 'Unfiled Documents' :
        (selectedFolder?.id === folderId ? selectedFolder.name : 'the folder');

      showSuccess(`Document moved to ${folderName}`);

      // Refresh both folder tree (to update counts) and document list
      setFoldersKey(prev => prev + 1);
      setDocumentsKey(prev => prev + 1);
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to move document');
    }
  }, [selectedFolder, showSuccess, showError]);

  const handleDocumentClick = useCallback(async (document) => {
    // If document has minimal data (from folder tree), fetch full details
    if (!document.file_size || !document.mime_type) {
      try {
        const response = await documentService.getDocument(document.id);
        setPreviewDocument(response.data);
      } catch (err) {
        console.error('Failed to load document details:', err);
        showError('Failed to load document details');
        return;
      }
    } else {
      setPreviewDocument(document);
    }
    setShowDocumentPreview(true);
  }, [showError]);

  const handleCloseDocumentPreview = useCallback(() => {
    setShowDocumentPreview(false);
    setPreviewDocument(null);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error || !caseData) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">
          <Briefcase className="h-16 w-16 mx-auto" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Case Not Found</h2>
        <p className="text-gray-600 mb-4">{error || 'The requested case could not be found.'}</p>
        <Link
          to="/cases"
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600"
        >
          Back to Cases
        </Link>
      </div>
    );
  }

  const primaryClient = caseClients.find(p => p.client?.id === caseData.client_id)?.client;
  const breadcrumbItems = primaryClient
    ? [{ label: primaryClient.company_name || `${primaryClient.first_name} ${primaryClient.last_name}`, icon: Users, to: `/clients/${primaryClient.id}` }]
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div>
            <Breadcrumb items={breadcrumbItems} />
            <div className="flex items-center space-x-3">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Briefcase className="mr-3 h-8 w-8" />
                {caseData.title}
              </h1>
              <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(caseData.status)}`}>
                {caseData.status}
              </span>
              {caseData.visibility_mode && caseData.visibility_mode !== 'firm' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-700" title={caseData.visibility_mode === 'include' ? 'Restricted: visible to selected users only' : 'Restricted: some users excluded'}>
                  <Lock className="w-3 h-3" />
                  Restricted
                </span>
              )}
            </div>
            <p className="text-gray-600">Case #{caseData.case_number}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowVisibilityModal(true)}
            className="bg-white text-gray-700 border border-gray-300 px-4 py-2 rounded-lg flex items-center hover:bg-gray-50"
            title="Manage case visibility"
          >
            <Shield className="mr-2 h-4 w-4" />
            Visibility
          </button>
          <button
            onClick={() => setShowAIChat(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-purple-700"
          >
            <Bot className="mr-2 h-4 w-4" />
            AI Chat
          </button>
          <button
            onClick={handleEditCase}
            className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600"
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit Case
          </button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-lg shadow px-4 py-2 flex items-center gap-1 overflow-x-auto">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mr-2 flex-shrink-0">Jump to</span>
        {[
          { id: 'section-overview', label: 'Overview', icon: Briefcase },
          { id: 'section-parties', label: 'Parties', icon: Users },
          { id: 'section-projects', label: 'Projects', icon: FolderOpen },
          { id: 'section-timeline', label: 'Timeline', icon: Calendar },
          { id: 'section-events', label: 'Events', icon: Calendar },
          { id: 'section-notes', label: 'Notes', icon: StickyNote },
          { id: 'section-comms', label: 'Comms', icon: MessageSquare },
          { id: 'section-documents', label: 'Documents', icon: FileText },
          { id: 'section-financials', label: 'Financials', icon: DollarSign },
          ...(caseData.requires_trust_funds ? [{ id: 'section-trust', label: 'Trust', icon: Landmark }] : []),
          { id: 'section-time', label: 'Time Entries', icon: Clock },
          { id: 'section-expenses', label: 'Expenses', icon: Receipt },
        ].map(({ id: sectionId, label, icon: Icon }) => (
          <button
            key={sectionId}
            onClick={() => document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-yellow-700 hover:bg-yellow-50 rounded-md transition-colors whitespace-nowrap flex-shrink-0"
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Case Overview */}
          <div id="section-overview" className="bg-white rounded-lg shadow p-6 scroll-mt-20">
            <h2 className="text-xl font-semibold mb-4">Case Overview</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Case Type</label>
                <p className="text-gray-900 capitalize">{caseData.case_type}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Court</label>
                <p className="text-gray-900">{caseData.court || 'Not specified'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Created</label>
                <p className="text-gray-900">
                  {new Date(caseData.created_at).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Last Updated</label>
                <p className="text-gray-900">
                  {new Date(caseData.updated_at).toLocaleDateString()}
                </p>
              </div>
            </div>
            {caseData.description && (
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-gray-900 mt-1">{caseData.description}</p>
              </div>
            )}
          </div>

          {/* Parties & Persons */}
          <div id="section-parties" className="scroll-mt-20">
            <CasePartiesSection caseId={id} />
          </div>

          {/* Projects */}
          <div id="section-projects" className="bg-white rounded-lg shadow p-6 scroll-mt-20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Projects</h2>
              <button
                onClick={() => setShowProjectModal(true)}
                className="bg-green-600 text-white px-3 py-1 rounded text-sm flex items-center hover:bg-green-700"
              >
                <Plus className="mr-1 h-4 w-4" />
                New Project
              </button>
            </div>
            
            {projects.length === 0 ? (
              <div className="text-center py-8">
                <FolderOpen className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600">No projects yet</p>
                <p className="text-gray-500 text-sm">Create your first project to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => navigate(`/projects/${project.id}`)}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:border-blue-300 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{project.name}</h3>
                        <p className="text-sm text-gray-600">{project.description}</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs rounded-full ${getProjectStatusColor(project.status)}`}>
                          {project.status}
                        </span>
                        {project.due_date && (
                          <p className="text-xs text-gray-500 mt-1 flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            Due {new Date(project.due_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Case Timeline & Scheduling */}
          <div id="section-timeline" className="scroll-mt-20">
            <CaseTimeline caseId={id} />
          </div>

          {/* Calendar Events Section */}
          <div id="section-events" className="scroll-mt-20">
            <CaseCalendarEvents
              caseId={id}
              onSuccess={showSuccess}
              onError={showError}
            />
          </div>

          {/* Case Notes Section */}
          <div id="section-notes" className="scroll-mt-20">
          <CaseNotesSection
            caseId={id}
            onSuccess={showSuccess}
            onError={showError}
            autoOpen={autoOpenNoteEditor}
            onAutoOpenHandled={() => setAutoOpenNoteEditor(false)}
          />
          </div>

          {/* Communications Section */}
          <div id="section-comms" className="scroll-mt-20">
            <CaseCommunicationsSection caseId={id} clientId={caseData.client_id} />
          </div>

          {/* Documents Section */}
          <div id="section-documents" className="bg-white rounded-lg shadow overflow-hidden scroll-mt-20">
            <div className="flex">
              {/* Left: Folder Sidebar */}
              <div className="w-64 border-r border-gray-200 flex-shrink-0">
                <FolderTree
                  key={foldersKey}
                  caseId={id}
                  selectedFolder={selectedFolder}
                  onSelectFolder={handleSelectFolder}
                  onCreateFolder={handleCreateFolder}
                  onEditFolder={handleEditFolder}
                  onDeleteFolder={handleDeleteFolder}
                  onDropDocument={handleDropDocument}
                  onDocumentClick={handleDocumentClick}
                  onRefresh={() => setDocumentsKey(prev => prev + 1)}
                />
              </div>

              {/* Right: Document Upload & List */}
              <div className="flex-1 p-6">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold flex items-center">
                      <FileText className="mr-2 h-5 w-5" />
                      Documents
                      {selectedFolder && (
                        <span className="text-sm text-gray-600 ml-2">
                          / {selectedFolder.id === 'unfiled' ? 'Unfiled Documents' : selectedFolder.name}
                        </span>
                      )}
                    </h2>
                    <Link
                      to={`/cases/${id}/documents`}
                      className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      title="Open full screen documents"
                    >
                      <Maximize2 className="h-4 w-4" />
                      Full Screen
                    </Link>
                  </div>

                  <DocumentUpload
                    caseId={id}
                    folderId={selectedFolder?.id !== 'unfiled' ? selectedFolder?.id : null}
                    onUploadSuccess={handleDocumentUpload}
                    multiple={true}
                  />
                </div>

                <DocumentList
                  key={documentsKey}
                  caseId={id}
                  folderId={selectedFolder?.id}
                  limit={20}
                  showActions={true}
                  onArchive={() => setFoldersKey(prev => prev + 1)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <Users className="mr-2 h-5 w-5" />
                Client{caseClients.length > 1 ? 's' : ''} Information
              </h3>
              <div className="relative" ref={clientPickerRef}>
                <button
                  onClick={() => setShowClientPicker(!showClientPicker)}
                  className="p-1 rounded-full hover:bg-yellow-100 text-yellow-600 hover:text-yellow-700 transition-colors"
                  title="Add client to case"
                >
                  <Plus className="h-5 w-5" />
                </button>
                {showClientPicker && (
                  <div className="absolute right-0 top-8 w-72 bg-white rounded-lg shadow-lg border z-50 p-3">
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search clients..."
                        value={clientSearch}
                        onChange={(e) => handleClientSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        autoFocus
                      />
                    </div>
                    {clientSearchLoading && (
                      <p className="text-xs text-gray-400 py-2 text-center">Searching...</p>
                    )}
                    {!clientSearchLoading && clientSearch && clientResults.length === 0 && (
                      <p className="text-xs text-gray-500 py-2 text-center">No clients found</p>
                    )}
                    {clientResults.length > 0 && (
                      <ul className="max-h-48 overflow-y-auto divide-y">
                        {clientResults.map((c) => {
                          const displayName = c.company_name
                            ? c.company_name
                            : [c.first_name, c.last_name].filter(Boolean).join(' ');
                          return (
                            <li key={c.id}>
                              <button
                                onClick={() => handleAddClient(c.id)}
                                className="w-full text-left px-2 py-2 hover:bg-yellow-50 rounded text-sm"
                              >
                                <span className="font-medium text-gray-900">{displayName}</span>
                                {c.email && <span className="block text-xs text-gray-500">{c.email}</span>}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
            {caseClients.length > 0 ? (
              <div className="space-y-4">
                {caseClients.map((party) => {
                  const client = party.client;
                  const name = client.company_name
                    ? client.company_name
                    : [client.first_name, client.last_name].filter(Boolean).join(' ');
                  const isPrimary = client.id === caseData.client_id;
                  const hasAddress = client.address_line1 || client.city || client.state_province || client.postal_code;
                  return (
                    <div key={party.id} className={`flex items-start justify-between ${caseClients.length > 1 ? 'pb-4 border-b last:border-b-0 last:pb-0' : ''}`}>
                      <div>
                        <Link to={`/clients/${client.id}`} className="font-medium text-blue-600 hover:text-blue-800 hover:underline">
                          {name}
                        </Link>
                        {client.email && <p className="text-sm text-gray-600">{client.email}</p>}
                        {client.phone && <p className="text-sm text-gray-600">{client.phone}</p>}
                        {hasAddress && (
                          <p className="text-sm text-gray-600 mt-1 flex items-start">
                            <MapPin className="h-3.5 w-3.5 mr-1 mt-0.5 flex-shrink-0 text-gray-400" />
                            <span>
                              {client.address_line1 && <>{client.address_line1}<br /></>}
                              {client.address_line2 && <>{client.address_line2}<br /></>}
                              {(client.city || client.state_province || client.postal_code) && (
                                <>
                                  {client.city}{client.city && client.state_province && ', '}
                                  {client.state_province}{client.postal_code && ' ' + client.postal_code}
                                </>
                              )}
                            </span>
                          </p>
                        )}
                      </div>
                      {!isPrimary && (
                        <button
                          onClick={() => handleRemoveClient(party.id)}
                          className="ml-2 p-1 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                          title="Remove client from case"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : caseData.client ? (
              <div>
                <Link
                  to={`/communications?client=${caseData.client_id}`}
                  className="font-medium text-yellow-600 hover:text-yellow-700 hover:underline"
                >
                  {caseData.client.name}
                </Link>
                <p className="text-sm text-gray-600">{caseData.client.email}</p>
                <p className="text-sm text-gray-600">{caseData.client.phone}</p>
              </div>
            ) : (
              <p className="text-gray-500">No client assigned</p>
            )}
          </div>

          {/* Financial Summary */}
          <div id="section-financials" className="bg-white rounded-lg shadow p-6 scroll-mt-20">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <DollarSign className="mr-2 h-5 w-5" />
              Financial Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Hours Worked</span>
                <span className="font-medium">{caseData.total_hours || 0}h</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Billable Amount</span>
                <span className="font-medium">${caseData.billable_amount || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount Paid</span>
                <span className="font-medium">${caseData.amount_paid || 0}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-gray-600">Outstanding</span>
                <span className="font-medium text-red-600">
                  ${(caseData.billable_amount || 0) - (caseData.amount_paid || 0)}
                </span>
              </div>
            </div>
          </div>

          {/* Trust Account */}
          {caseData.requires_trust_funds && (
            <div id="section-trust" className="scroll-mt-20">
              <CaseTrustSection
                caseId={id}
                clientId={caseData.client_id}
                clientName={caseData.client?.name}
              />
            </div>
          )}

          {/* Time Tracking */}
          <div id="section-time" className="scroll-mt-20">
          <CaseTimeTracking
            caseId={id}
            onSuccess={showSuccess}
            onError={showError}
          />
          </div>

          {/* Expenses */}
          <div id="section-expenses" className="scroll-mt-20">
          <CaseExpenses caseId={id} />
          </div>

          {/* Deadlines */}
          <DeadlineManager
            caseId={id}
            onDeadlineChange={() => {}}
          />

          {/* Risk Assessment */}
          <SchedulingRiskDashboard caseId={id} />

        </div>
      </div>

      {/* Edit Case Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Case</h3>

            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Case Number</label>
                  <input
                    type="text"
                    value={editForm.case_number}
                    onChange={(e) => setEditForm({...editForm, case_number: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => {
                      setEditForm({
                        ...editForm,
                        status: e.target.value,
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned Attorney</label>
                <select
                  value={editForm.assigned_attorney_id || ''}
                  onChange={(e) => setEditForm({...editForm, assigned_attorney_id: e.target.value || null})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">Select Attorney</option>
                  {attorneys.map(att => (
                    <option key={att.id} value={att.id}>{att.first_name} {att.last_name}</option>
                  ))}
                </select>
              </div>


              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case Type</label>
                <input
                  type="text"
                  value={editForm.case_type}
                  onChange={(e) => setEditForm({...editForm, case_type: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="e.g. Personal Injury, Contract Law, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Type</label>
                  <select
                    value={editForm.billing_type}
                    onChange={(e) => setEditForm({...editForm, billing_type: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="flat_fee">Flat Fee</option>
                    <option value="contingency">Contingency</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Estimated Value</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.total_estimated_value}
                    onChange={(e) => setEditForm({...editForm, total_estimated_value: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              {editForm.billing_type === 'flat_fee' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Flat Fee Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.flat_fee_amount}
                    onChange={(e) => setEditForm({...editForm, flat_fee_amount: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              )}

              {editForm.billing_type === 'contingency' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contingency Percentage</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={editForm.contingency_percentage}
                    onChange={(e) => setEditForm({...editForm, contingency_percentage: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editForm.requires_trust_funds}
                  onChange={(e) => setEditForm({...editForm, requires_trust_funds: e.target.checked})}
                  className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 mr-2"
                />
                <label className="text-sm text-gray-700">Requires Trust Funds</label>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSaveCase}
                className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
              >
                Save Changes
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Project Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Create New Project</h3>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                <input
                  type="text"
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Enter project name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Project description"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Value</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={projectForm.estimated_value}
                    onChange={(e) => setProjectForm({...projectForm, estimated_value: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Hours</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={projectForm.estimated_hours}
                    onChange={(e) => setProjectForm({...projectForm, estimated_hours: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder="0.0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={projectForm.status}
                    onChange={(e) => setProjectForm({...projectForm, status: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="on_hold">On Hold</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={projectForm.priority}
                    onChange={(e) => setProjectForm({...projectForm, priority: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={projectForm.start_date}
                    onChange={(e) => setProjectForm({...projectForm, start_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={projectForm.due_date}
                    onChange={(e) => setProjectForm({...projectForm, due_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={projectForm.requires_payment_first}
                  onChange={(e) => setProjectForm({...projectForm, requires_payment_first: e.target.checked})}
                  className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500 mr-2"
                />
                <label className="text-sm text-gray-700">Requires Payment First</label>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleCreateProject}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Create Project
              </button>
              <button
                onClick={() => setShowProjectModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Interface */}
      <AIChatInterface
        caseId={id}
        isOpen={showAIChat}
        onClose={() => setShowAIChat(false)}
      />

      {/* Folder Modals */}
      <NewFolderModal
        isOpen={showFolderModal}
        onClose={() => {
          setShowFolderModal(false);
          setEditingFolder(null);
          setParentFolderForNew(null);
        }}
        onSave={handleSaveFolder}
        caseId={id}
        parentFolder={parentFolderForNew}
        editingFolder={editingFolder}
      />

      <ConfirmDialog
        isOpen={showDeleteFolderConfirm}
        onClose={() => {
          setShowDeleteFolderConfirm(false);
          setFolderToDelete(null);
        }}
        onConfirm={confirmDeleteFolder}
        title="Delete Folder"
        message={`Are you sure you want to delete "${folderToDelete?.name}"? Documents in this folder will become unfiled.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Document Preview Modal */}
      <DocumentPreview
        document={previewDocument}
        isOpen={showDocumentPreview}
        onClose={handleCloseDocumentPreview}
      />

      {/* Send for Signature Modal */}
      <SendDocumentModal
        isOpen={showSigningModal}
        onClose={() => setShowSigningModal(false)}
        onSuccess={() => {
          setShowSigningModal(false);
          showSuccess('Document sent for signature');
        }}
        caseId={id}
        clientId={caseData?.client_id}
      />

      {/* Case Visibility Modal */}
      {showVisibilityModal && (
        <CaseVisibilityModal
          caseId={id}
          caseName={caseData?.title}
          onClose={() => setShowVisibilityModal(false)}
          onSaved={() => fetchCaseData()}
        />
      )}

      {/* Notification Toast */}
      <Notification
        isOpen={notification.isOpen}
        onClose={hideNotification}
        title={notification.title}
        message={notification.message}
        variant={notification.variant}
        duration={notification.duration}
      />
    </div>
  );
};

export default CaseDetail;